import React, { useMemo } from "react";

import DropdownSelector from "../../../Components/DropdownSelector";
import { useHealthScoreThresholds } from "../../../Hooks/useHealthScoreThresholds";
import {
  formatAccountHealthBandLegend,
  formatBusinessHealthBandLegend,
} from "../../../Utils/HealthThresholds";
import type { PortfolioNeedsAttentionSettings } from "../PortfolioNeedsAttentionOrgSettings";

const RENEWAL_OPTIONS = [
  { label: "Off (not active)", value: 0 },
  { label: "Within 30 days", value: 30 },
  { label: "Within 60 days", value: 60 },
  { label: "Within 90 days", value: 90 },
  { label: "Within 180 days", value: 180 },
];

function ClusterCheckOption({
  checked,
  onChange,
  label,
  detail,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  detail: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700">
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 accent-purple-600"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
      />
      <span>
        {label}
        <span className="text-gray-400"> ({detail})</span>
      </span>
    </label>
  );
}

export interface NeedsAttentionCriteriaFormProps {
  value: PortfolioNeedsAttentionSettings;
  onChange: (patch: Partial<PortfolioNeedsAttentionSettings>) => void;
}

/** Extracted from PortfolioNeedsAttentionOrgSettings/index.tsx so the same
 * editable criteria can also be embedded inline (the top-bar tile popover,
 * the Portfolio Settings modal) instead of only on its own settings page —
 * mirrors LowScoreCriteriaForm's separation of form from load/save shell. */
const NeedsAttentionCriteriaForm: React.FC<NeedsAttentionCriteriaFormProps> = ({
  value,
  onChange,
}) => {
  const { thresholds } = useHealthScoreThresholds();

  const bandDetails = useMemo(() => {
    const ah = formatAccountHealthBandLegend(thresholds.account_health);
    const bh = formatBusinessHealthBandLegend(thresholds.business_health);
    return {
      triage: ah.triage.replace(/^Triage\s+/i, "Account Health "),
      defend: ah.defend.replace(/^Defend\s+/i, "Account Health "),
      bhConcern: bh.concern.replace(/^Concern\s+/i, "Business Health "),
    };
  }, [thresholds]);

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-1 text-sm font-semibold text-gray-800">
          Account Health threshold
        </div>
        <p className="mb-2 text-xs text-gray-500">
          Flag accounts with Account Health below this value (default: 65).
        </p>
        <div className="flex max-w-md items-center gap-3">
          <input
            type="range"
            min={50}
            max={85}
            step={1}
            value={value.health_threshold}
            onChange={e =>
              onChange({ health_threshold: Number(e.target.value) })
            }
            className="flex-1"
          />
          <div className="w-8 text-right text-sm font-bold text-gray-700">
            {value.health_threshold}
          </div>
        </div>
      </div>

      <div>
        <div className="mb-1 text-sm font-semibold text-gray-800">
          Renewal proximity
        </div>
        <p className="mb-2 text-xs text-gray-500">
          Flag accounts with renewal within N days.
        </p>
        <div className="max-w-[240px]">
          <DropdownSelector
            label="Renewal proximity"
            selected={String(value.renewal_days)}
            options={RENEWAL_OPTIONS.map(opt => ({
              label: opt.label,
              value: String(opt.value),
            }))}
            onSelect={v => onChange({ renewal_days: Number(v) })}
            triggerStyle="field"
            enableScroll
            fullWidth
            hideLabel
          />
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-gray-800">
          Cluster inclusion
        </div>
        <div className="flex flex-col gap-2.5">
          <ClusterCheckOption
            checked={value.include_triage}
            onChange={checked => onChange({ include_triage: checked })}
            label="Triage — include all Triage cluster accounts"
            detail={bandDetails.triage}
          />
          <ClusterCheckOption
            checked={value.include_defend}
            onChange={checked => onChange({ include_defend: checked })}
            label="Defend — include all Defend cluster accounts"
            detail={bandDetails.defend}
          />
          <ClusterCheckOption
            checked={value.include_bh_concern}
            onChange={checked => onChange({ include_bh_concern: checked })}
            label="Include all BH Concern accounts"
            detail={bandDetails.bhConcern}
          />
          <ClusterCheckOption
            checked={value.include_unscored}
            onChange={checked => onChange({ include_unscored: checked })}
            label="Include all Unavailable accounts"
            detail="no KPI data / no Account Health score"
          />
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-gray-800">
          Relationship signals
        </div>
        <div className="flex flex-col gap-2.5">
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-purple-600"
              checked={value.include_unmapped}
              onChange={e => onChange({ include_unmapped: e.target.checked })}
            />
            <span>
              No pairs mapped (My RL) — flag accounts with no mapped
              relationships
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-purple-600"
              checked={value.include_unknown_al}
              onChange={e => onChange({ include_unknown_al: e.target.checked })}
            />
            <span>
              No Account Level assessment — flag accounts without a computed
              Account Level
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default NeedsAttentionCriteriaForm;
